/**
 * Performance Optimization Service
 * Addresses Core Web Vitals and performance metrics
 */

export interface PerformanceMetrics {
  fcp: number; // First Contentful Paint
  lcp: number; // Largest Contentful Paint
  tbt: number; // Total Blocking Time
  cls: number; // Cumulative Layout Shift
  si: number;  // Speed Index
}

export interface OptimizationConfig {
  enablePreloading: boolean;
  enableCodeSplitting: boolean;
  enableImageOptimization: boolean;
  enableResourceHints: boolean;
  enableServiceWorker: boolean;
  enableCriticalCSS: boolean;
}

/**
 * Critical Resource Preloader
 * Preloads essential resources to improve LCP and FCP
 */
export class CriticalResourcePreloader {
  private preloadedResources = new Set<string>();

  preloadCriticalResources() {
    // Only preload resources that are actually used
    // Preload critical fonts only if they're not already loaded
    if (!document.querySelector('link[href*="fonts.googleapis.com"]')) {
      // Fonts are loaded via CSS imports, no need to preload
    }
    
    const logoElement = document.querySelector('img[src*="stringpak-main-logo"], img[src*="logo"]');
    if (logoElement) {
      const src = logoElement.getAttribute('src');
      if (src && !this.preloadedResources.has(src)) {
        this.preloadResource(src, 'image');
        this.preloadedResources.add(src);
      }
    }
    
    // Preload favicon if not already present
    if (!document.querySelector('link[rel="icon"]')) {
      // Favicon is handled by HTML
    }
  }

  private preloadResource(href: string, as: string) {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = href;
    link.as = as;
    document.head.appendChild(link);
  }
}

/**
 * Initialize performance optimizations
 * This function is called at app startup
 */
export function initializePerformanceOptimizations(): void {
  // Initialize critical resource preloader
  const preloader = new CriticalResourcePreloader();
  preloader.preloadCriticalResources();
  
  // Additional performance optimizations can be added here
  if (process.env.NODE_ENV === 'production') {
    // Production-only optimizations
  }
}
